// src/services/reportService.js
import apiClient from '../api/apiClient';
import { getCurrentAnalysisId } from '../utils/appStateManagerAPI';

/**
 * Enhanced Report Service
 * Handles comprehensive report generation with KPIs, charts, and professional formatting using database data only
 */
class ReportService {
  constructor() {
    this.baseURL = '/reports';
    this.progressCallbacks = new Map();
    this.currentGenerationId = null;
    this.activeRequests = new Map(); // Track active requests to prevent duplicates
    this.requestCache = new Map(); // Cache recent results
    this.cacheTimeout = 30000; // 30 seconds cache timeout
  }

  /**
   * Generate enhanced comprehensive report with progress tracking and deduplication
   * @param {string} analysisId - The analysis ID
   * @param {Object} options - Generation options
   * @param {Function} progressCallback - Progress callback function
   * @returns {Promise<Object>} Enhanced report data
   */
  async generateComprehensiveReport(analysisId, options = {}, progressCallback = null) {
    if (!analysisId) {
      throw new Error('Analysis ID is required');
    }

    const requestKey = `comprehensive-${analysisId}`;
    
    // Check if there's already an active request for this analysis
    if (this.activeRequests.has(requestKey)) {
      console.log(`[ReportService] Request already in progress for analysis: ${analysisId}, returning existing promise`);
      return this.activeRequests.get(requestKey);
    }

    // Check cache first
    const cachedResult = this.getCachedResult(requestKey);
    if (cachedResult && !options.forceRefresh) {
      console.log(`[ReportService] Returning cached result for analysis: ${analysisId}`);
      if (progressCallback) {
        progressCallback({ progress: 100, message: 'Rapport récupéré depuis le cache', timestamp: new Date().toISOString() });
      }
      return cachedResult;
    }

    const generationId = Date.now().toString();
    this.currentGenerationId = generationId;
    
    if (progressCallback) {
      this.progressCallbacks.set(generationId, progressCallback);
    }

    // Create the request promise
    const requestPromise = this.executeReportGeneration(analysisId, generationId);
    
    // Store the active request
    this.activeRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result
      this.setCachedResult(requestKey, result);
      
      return result;
    } catch (error) {
      console.error('[ReportService] Error generating enhanced comprehensive report:', error);
      throw this.handleEnhancedError(error);
    } finally {
      // Cleanup
      this.activeRequests.delete(requestKey);
      this.progressCallbacks.delete(generationId);
      if (this.currentGenerationId === generationId) {
        this.currentGenerationId = null;
      }
    }
  }

  /**
   * Execute the actual report generation using database data only
   * @private
   */
  async executeReportGeneration(analysisId, generationId) {
    console.log(`[ReportService] Generating database-driven comprehensive report for analysis: ${analysisId}`);

    // Step 1: Initialize
    this.updateProgress(generationId, 0, 'Initialisation de la génération du rapport...');

    // Step 2: Generate base report data from database
    this.updateProgress(generationId, 30, 'Collecte des données depuis la base de données...');
    const response = await apiClient.get(`${this.baseURL}/comprehensive/${analysisId}`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to generate report');
    }

    const reportData = response.data;

    // Step 3: Validate database data
    this.updateProgress(generationId, 70, 'Validation des données de la base...');
    this.validateDatabaseData(reportData);

    // Step 4: Finalize with database-only content
    this.updateProgress(generationId, 100, 'Rapport généré avec succès depuis la base de données');

    console.log(`[ReportService] Database-driven report generated successfully`);
    console.log(`[ReportService] Data sources:`, this.getDataSourcesSummary(reportData));

    return reportData;
  }

  /**
   * Generate report with optimized single request approach
   * @param {string} analysisId - The analysis ID
   * @param {Object} options - Generation options
   * @param {Function} progressCallback - Progress callback function
   * @returns {Promise<Object>} Enhanced report data
   */
  async generateReportWithServices(analysisId, options = {}, progressCallback = null) {
    const generationId = Date.now().toString();
    this.currentGenerationId = generationId;
    
    if (progressCallback) {
      this.progressCallbacks.set(generationId, progressCallback);
    }

    try {
      console.log(`[ReportService] Generating optimized report for analysis: ${analysisId}`);

      if (!analysisId) {
        throw new Error('Analysis ID is required');
      }

      // Check if there's already a generation in progress for this analysis
      if (this.currentGenerationId && this.currentGenerationId !== generationId) {
        console.warn(`[ReportService] Report generation already in progress, cancelling duplicate request`);
        throw new Error('Report generation already in progress');
      }

      this.updateProgress(generationId, 10, 'Initialisation de la génération optimisée...');

      // Use the main comprehensive report endpoint which includes all data
      this.updateProgress(generationId, 30, 'Récupération de toutes les données...');
      
      const response = await apiClient.get(`${this.baseURL}/comprehensive/${analysisId}`);
      
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to generate comprehensive report');
      }

      const reportData = response.data;

      // Add generation metadata
      reportData.generationMetadata = {
        ...reportData.generationMetadata,
        generatedAt: new Date().toISOString(),
        optimizedGeneration: true,
        services: {
          baseReport: true,
          kpiCalculation: !!reportData.kpiData,
          chartGeneration: !!reportData.chartData,
          aiSynthesis: !!reportData.aiInsights,
          logoManagement: !!reportData.logoData
        }
      };

      this.updateProgress(generationId, 80, 'Validation des données...');
      this.validateEnhancedData(reportData);

      this.updateProgress(generationId, 100, 'Rapport généré avec succès');

      console.log(`[ReportService] Optimized report generated successfully`);
      console.log(`[ReportService] Services status:`, reportData.generationMetadata.services);
      
      return reportData;

    } catch (error) {
      console.error('[ReportService] Error generating optimized report:', error);
      this.updateProgress(generationId, -1, `Erreur: ${error.message}`);
      throw this.handleEnhancedError(error);
    } finally {
      this.progressCallbacks.delete(generationId);
      if (this.currentGenerationId === generationId) {
        this.currentGenerationId = null;
      }
    }
  }

  /**
   * Get report generation status for an analysis
   * @param {string} analysisId - The analysis ID
   * @returns {Promise<Object>} Status information
   */
  async getReportStatus(analysisId) {
    try {
      console.log(`[ReportService] Checking report status for analysis: ${analysisId}`);

      if (!analysisId) {
        throw new Error('Analysis ID is required');
      }

      const response = await apiClient.get(`${this.baseURL}/status/${analysisId}`);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get report status');
      }

      return response.data;

    } catch (error) {
      console.error('[ReportService] Error getting report status:', error);
      throw error;
    }
  }

  /**
   * Download enhanced report as professional HTML
   * @param {Object} reportData - The enhanced report data
   * @param {string} filename - The filename for download
   * @param {Object} options - Download options
   * @param {Function} progressCallback - Progress callback function
   * @returns {Promise<void>}
   */
  async downloadReport(reportData, filename = 'rapport-ebios-rm.html', options = {}, progressCallback = null) {
    const generationId = Date.now().toString();
    
    if (progressCallback) {
      this.progressCallbacks.set(generationId, progressCallback);
    }

    try {
      console.log(`[ReportService] Generating database-driven professional EBIOS RM HTML report: ${filename}`);

      // Step 1: Initialize HTML generation
      this.updateProgress(generationId, 10, 'Initialisation de la génération HTML...');

      // Step 2: Prepare database data with completeness indicators
      this.updateProgress(generationId, 20, 'Préparation et validation des données de la base...');
      
      const {
        kpiData = {},
        chartData = {},
        logoData = {},
        ...baseReportData
      } = reportData;

      // Add data completeness summary based on database content
      const dataCompleteness = this.calculateReportCompleteness(reportData);
      const enhancedReportData = {
        ...baseReportData,
        dataCompleteness,
        generationMetadata: {
          ...reportData.generationMetadata,
          databaseGeneration: true,
          completenessScore: dataCompleteness.overallScore,
          generatedAt: new Date().toISOString()
        }
      };

      // Step 3: Initialize the database-driven HTML report generator
      this.updateProgress(generationId, 40, 'Initialisation du générateur HTML...');
      
      const { EnhancedHtmlReportGenerator } = await import('../utils/enhancedHtmlReportGenerator');
      
      // Step 4: Generate HTML using database data only
      this.updateProgress(generationId, 60, 'Génération du rapport HTML depuis la base de données...');
      
      const generator = new EnhancedHtmlReportGenerator(
        enhancedReportData,
        kpiData,
        chartData,
        logoData
      );

      // Step 5: Generate the complete HTML report
      this.updateProgress(generationId, 80, 'Finalisation du rapport...');
      
      const htmlBlob = await generator.generateCompleteReport();

      // Step 6: Download the file
      this.updateProgress(generationId, 90, 'Préparation du téléchargement...');
      
      // Create download link with enhanced UX
      const url = window.URL.createObjectURL(htmlBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Add to DOM, trigger download, and cleanup
      document.body.appendChild(link);
      link.click();
      
      // Cleanup with delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      this.updateProgress(generationId, 100, 'Rapport HTML téléchargé avec succès');

      console.log(`[ReportService] Database-driven professional HTML report generated successfully: ${filename}`);

    } catch (error) {
      console.error('[ReportService] Error generating enhanced professional HTML report:', error);
      this.updateProgress(generationId, -1, `Erreur: ${error.message}`);
      throw new Error('Erreur lors de la génération du rapport HTML professionnel');
    } finally {
      this.progressCallbacks.delete(generationId);
    }
  }

  /**
   * Download report with PDF option (HTML is primary format now)
   * @param {Object} reportData - The report data
   * @param {string} filename - The filename for download
   * @param {string} format - Format ('html' or 'pdf')
   * @param {Function} progressCallback - Progress callback function
   * @returns {Promise<void>}
   */
  async downloadReportWithFormat(reportData, filename = 'rapport-ebios-rm', format = 'html', progressCallback = null) {
    const downloadKey = `download-${filename}-${format}`;
    
    // Prevent multiple simultaneous downloads
    if (this.activeRequests.has(downloadKey)) {
      console.log(`[ReportService] Download already in progress for: ${filename}`);
      return this.activeRequests.get(downloadKey);
    }

    const downloadPromise = this.executeDownload(reportData, filename, format, progressCallback);
    this.activeRequests.set(downloadKey, downloadPromise);

    try {
      await downloadPromise;
    } finally {
      this.activeRequests.delete(downloadKey);
    }
  }

  /**
   * Execute the actual download
   * @private
   */
  async executeDownload(reportData, filename, format, progressCallback) {
    try {
      if (format === 'html') {
        // Primary HTML format
        await this.downloadReport(reportData, `${filename}.html`, {}, progressCallback);
      } else if (format === 'pdf') {
        console.warn('[ReportService] PDF generation is deprecated, using HTML format');
        await this.downloadReport(reportData, `${filename}.html`, {}, progressCallback);
      } else {
        throw new Error(`Format non supporté: ${format}`);
      }
    } catch (error) {
      console.error(`[ReportService] Report generation failed for format ${format}:`, error);
      throw new Error(`Impossible de générer le rapport au format ${format}`);
    }
  }

  /**
   * Generate report for current analysis
   * @returns {Promise<Object>} Report data
   */
  async generateCurrentAnalysisReport() {
    const analysisId = getCurrentAnalysisId();
    
    if (!analysisId) {
      throw new Error('Aucune analyse active trouvée');
    }

    return this.generateComprehensiveReport(analysisId);
  }

  /**
   * Check if report can be generated for current analysis
   * @returns {Promise<boolean>} Whether report can be generated
   */
  async canGenerateReport() {
    try {
      const analysisId = getCurrentAnalysisId();
      
      if (!analysisId) {
        return false;
      }

      const status = await this.getReportStatus(analysisId);
      return status.canGenerateReport;

    } catch (error) {
      console.error('[ReportService] Error checking if report can be generated:', error);
      return false;
    }
  }

  /**
   * Get report readiness information
   * @returns {Promise<Object>} Report readiness info
   */
  async getReportReadiness() {
    try {
      const analysisId = getCurrentAnalysisId();
      
      if (!analysisId) {
        return {
          ready: false,
          reason: 'Aucune analyse active',
          completedWorkshops: 0,
          totalWorkshops: 5
        };
      }

      const status = await this.getReportStatus(analysisId);
      
      return {
        ready: status.canGenerateReport,
        recommended: status.recommendedMinimum,
        reason: status.canGenerateReport 
          ? 'Rapport disponible' 
          : 'Complétez au moins un atelier',
        completedWorkshops: status.completedWorkshops,
        totalWorkshops: status.totalWorkshops,
        workshopStatus: status.workshopStatus,
        overallCompleteness: status.overallCompleteness
      };

    } catch (error) {
      console.error('[ReportService] Error getting report readiness:', error);
      return {
        ready: false,
        reason: 'Erreur lors de la vérification',
        completedWorkshops: 0,
        totalWorkshops: 5
      };
    }
  }

  /**
   * Test report service connectivity
   * @returns {Promise<boolean>} Whether service is available
   */
  async testConnection() {
    try {
      const response = await apiClient.get(`${this.baseURL}/test`);
      return response.success;
    } catch (error) {
      console.error('[ReportService] Connection test failed:', error);
      return false;
    }
  }

  /**
   * Format report data for display
   * @param {Object} reportData - Raw report data
   * @returns {Object} Formatted report data
   */
  formatReportData(reportData) {
    if (!reportData) return null;

    return {
      metadata: {
        ...reportData.metadata,
        formattedCreatedAt: new Date(reportData.metadata?.createdAt).toLocaleDateString('fr-FR'),
        formattedLastModified: new Date(reportData.metadata?.lastModified).toLocaleDateString('fr-FR'),
        formattedReportGenerated: new Date(reportData.metadata?.reportGeneratedAt).toLocaleDateString('fr-FR')
      },
      summary: {
        totalScenarios: reportData.executiveSummary?.totalRiskScenarios || 0,
        highRiskScenarios: reportData.executiveSummary?.highRiskScenarios || 0,
        implementedControls: reportData.executiveSummary?.implementedControls || 0,
        riskReduction: reportData.executiveSummary?.riskReduction || 0,
        completedWorkshops: Object.values(reportData.workshops || {})
          .filter(workshop => workshop.completionStatus === 'completed').length
      },
      workshops: reportData.workshops || {},
      analytics: reportData.analytics || {}
    };
  }

  /**
   * Get report summary for display
   * @param {string} analysisId - The analysis ID
   * @returns {Promise<Object>} Report summary
   */
  async getReportSummary(analysisId) {
    try {
      const reportData = await this.generateComprehensiveReport(analysisId);
      return this.formatEnhancedReportData(reportData);
    } catch (error) {
      console.error('[ReportService] Error getting report summary:', error);
      throw error;
    }
  }

  /**
   * Get individual service data
   * @param {string} analysisId - The analysis ID
   * @param {string} service - Service name (kpis, charts, ai-insights, logos)
   * @returns {Promise<Object>} Service data
   */
  async getServiceData(analysisId, service) {
    try {
      console.log(`[ReportService] Getting ${service} data for analysis: ${analysisId}`);

      if (!analysisId) {
        throw new Error('Analysis ID is required');
      }

      const response = await apiClient.get(`${this.baseURL}/${service}/${analysisId}`);

      if (!response.success) {
        throw new Error(response.error?.message || `Failed to get ${service} data`);
      }

      return response.data;

    } catch (error) {
      console.error(`[ReportService] Error getting ${service} data:`, error);
      throw error;
    }
  }

  /**
   * Get service health status
   * @returns {Promise<Object>} Health status
   */
  async getServiceHealth() {
    try {
      console.log('[ReportService] Checking service health');

      const response = await apiClient.get(`${this.baseURL}/health`);

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get service health');
      }

      return response.data;

    } catch (error) {
      console.error('[ReportService] Error getting service health:', error);
      return {
        overall: 'unknown',
        services: {},
        error: error.message
      };
    }
  }

  /**
   * AI service status disabled - using database-only mode
   * @returns {Promise<Object>} AI service status (always disabled)
   */
  async getAIStatus() {
    // AI service disabled - return immediate response
    return {
      aiStatus: 'disabled',
      message: 'AI service disabled - using database-only mode',
      databaseOnly: true
    };
  }

  /**
   * Cancel current report generation
   */
  cancelGeneration() {
    if (this.currentGenerationId) {
      console.log(`[ReportService] Cancelling generation: ${this.currentGenerationId}`);
      this.updateProgress(this.currentGenerationId, -1, 'Génération annulée');
      this.progressCallbacks.delete(this.currentGenerationId);
      this.currentGenerationId = null;
    }
    
    // Clear all active requests
    this.activeRequests.clear();
  }

  /**
   * Get cached result if available and not expired
   * @private
   */
  getCachedResult(requestKey) {
    const cached = this.requestCache.get(requestKey);
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      return cached.data;
    }
    
    // Remove expired cache entry
    if (cached) {
      this.requestCache.delete(requestKey);
    }
    
    return null;
  }

  /**
   * Set cached result
   * @private
   */
  setCachedResult(requestKey, data) {
    this.requestCache.set(requestKey, {
      data: data,
      timestamp: Date.now()
    });
    
    // Clean up old cache entries (keep only last 10)
    if (this.requestCache.size > 10) {
      const oldestKey = this.requestCache.keys().next().value;
      this.requestCache.delete(oldestKey);
    }
  }

  /**
   * Clear all cached results
   */
  clearCache() {
    this.requestCache.clear();
    console.log('[ReportService] Cache cleared');
  }

  /**
   * Update progress for report generation
   * @param {string} generationId - Generation ID
   * @param {number} progress - Progress percentage (0-100, -1 for error)
   * @param {string} message - Progress message
   */
  updateProgress(generationId, progress, message) {
    const callback = this.progressCallbacks.get(generationId);
    if (callback) {
      callback({
        progress,
        message,
        timestamp: new Date().toISOString(),
        generationId
      });
    }
  }

  /**
   * Validate database report data
   * @param {Object} reportData - Report data to validate
   */
  validateDatabaseData(reportData) {
    if (!reportData) {
      throw new Error('Aucune donnée de rapport reçue de la base de données');
    }

    if (!reportData.metadata) {
      console.warn('[ReportService] Missing metadata in database report data');
    }

    if (!reportData.workshops) {
      console.warn('[ReportService] Missing workshops data in database report');
    }

    // Check database data completeness
    const workshops = reportData.workshops || {};
    const workshopCount = Object.keys(workshops).length;
    
    console.log(`[ReportService] Database workshops available: ${workshopCount}/5`);

    if (workshopCount === 0) {
      throw new Error('Aucune donnée d\'atelier disponible dans la base de données');
    }

    // Log data completeness for each workshop
    Object.entries(workshops).forEach(([workshopKey, workshopData]) => {
      const dataKeys = Object.keys(workshopData).filter(key => 
        workshopData[key] && 
        (Array.isArray(workshopData[key]) ? workshopData[key].length > 0 : true)
      );
      console.log(`[ReportService] ${workshopKey}: ${dataKeys.length} data sections available`);
    });
  }

  /**
   * Get data sources summary for logging
   * @param {Object} reportData - Report data
   * @returns {Object} Data sources summary
   */
  getDataSourcesSummary(reportData) {
    const workshops = reportData.workshops || {};
    const summary = {};

    Object.entries(workshops).forEach(([workshopKey, workshopData]) => {
      summary[workshopKey] = {
        businessValues: workshopData.businessValues?.length || 0,
        dreadedEvents: workshopData.dreadedEvents?.length || 0,
        securityControls: workshopData.securityControls?.length || 0,
        riskSources: workshopData.riskSources?.length || 0,
        stakeholders: workshopData.stakeholders?.length || 0,
        strategicScenarios: workshopData.strategicScenarios?.length || 0,
        operationalScenarios: workshopData.operationalScenarios?.length || 0,
        riskTreatmentPlans: workshopData.riskTreatmentPlans?.length || 0,
        completionStatus: workshopData.completionStatus || 'not-started'
      };
    });

    return summary;
  }

  /**
   * Handle enhanced error responses
   * @param {Error} error - Original error
   * @returns {Error} Enhanced error
   */
  handleEnhancedError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 404:
          return new Error('Analyse non trouvée. Veuillez vérifier l\'ID d\'analyse.');
        case 400:
          return new Error(data.error?.message || 'Données d\'analyse invalides.');
        case 500:
          if (data.error?.code === 'INCOMPLETE_DATA') {
            return new Error('Données d\'atelier incomplètes. Complétez au moins un atelier pour générer un rapport.');
          }
          if (data.error?.code === 'PARTIAL_SERVICE_DEGRADATION') {
            const degradedServices = Object.entries(data.error.degradedServices || {})
              .filter(([_, degraded]) => degraded)
              .map(([service, _]) => service)
              .join(', ');
            return new Error(`Services dégradés (${degradedServices}). Le rapport peut être incomplet.`);
          }
          return new Error(data.error?.message || 'Erreur serveur lors de la génération du rapport.');
        default:
          return new Error(`Erreur HTTP ${status}: ${data.error?.message || 'Erreur inconnue'}`);
      }
    } else if (error.request) {
      return new Error('Erreur de connexion. Vérifiez votre connexion internet.');
    } else {
      return new Error(error.message || 'Erreur lors de la génération du rapport');
    }
  }

  /**
   * Format enhanced report data for display
   * @param {Object} reportData - Raw enhanced report data
   * @returns {Object} Formatted report data
   */
  formatEnhancedReportData(reportData) {
    if (!reportData) return null;

    const formatted = this.formatReportData(reportData);

    // Add enhanced data formatting
    if (reportData.kpiData) {
      formatted.kpis = {
        riskMetrics: reportData.kpiData.riskMetrics || {},
        controlMetrics: reportData.kpiData.controlMetrics || {},
        healthScore: reportData.kpiData.healthScore || {},
        businessValueMetrics: reportData.kpiData.businessValueMetrics || {},
        threatMetrics: reportData.kpiData.threatMetrics || {}
      };
    }

    if (reportData.aiInsights) {
      formatted.aiInsights = {
        available: !reportData.aiInsights.error,
        executiveSummary: reportData.aiInsights.executiveSummary || null,
        patterns: reportData.aiInsights.patterns || null,
        recommendations: reportData.aiInsights.recommendations || null,
        aiGenerated: reportData.aiInsights.executiveSummary?.aiGenerated || false
      };
    }

    if (reportData.chartData) {
      formatted.charts = {
        available: !reportData.chartData.error,
        riskDistribution: reportData.chartData.riskDistribution || null,
        controlEffectiveness: reportData.chartData.controlEffectiveness || null,
        timeline: reportData.chartData.timeline || null,
        businessValue: reportData.chartData.businessValue || null
      };
    }

    if (reportData.logoData) {
      formatted.branding = {
        available: !reportData.logoData.error,
        header: reportData.logoData.header || null,
        footer: reportData.logoData.footer || null,
        attributions: reportData.logoData.attributions || null
      };
    }

    // Add service status
    formatted.services = reportData.generationMetadata?.services || {};

    return formatted;
  }

  /**
   * Calculate report completeness for enhanced display
   */
  calculateReportCompleteness(reportData) {
    const workshops = reportData.workshops || {};
    let totalSections = 0;
    let completeSections = 0;
    const sectionDetails = {};

    // Analyze each workshop
    Object.keys(workshops).forEach(workshopKey => {
      const workshop = workshops[workshopKey];
      const workshopNumber = workshopKey.replace('workshop', '');
      
      totalSections++;
      
      let workshopComplete = false;
      let workshopScore = 0;
      
      switch (workshopNumber) {
        case '1':
          workshopComplete = (workshop.businessValues?.length > 0) && (workshop.dreadedEvents?.length > 0);
          workshopScore = ((workshop.businessValues?.length > 0 ? 50 : 0) + (workshop.dreadedEvents?.length > 0 ? 50 : 0));
          break;
        case '2':
          workshopComplete = (workshop.riskSources?.length > 0) && (workshop.stakeholders?.length > 0);
          workshopScore = ((workshop.riskSources?.length > 0 ? 50 : 0) + (workshop.stakeholders?.length > 0 ? 50 : 0));
          break;
        case '3':
          workshopComplete = (workshop.strategicScenarios?.length > 0);
          workshopScore = workshop.strategicScenarios?.length > 0 ? 100 : 0;
          break;
        case '4':
          workshopComplete = (workshop.operationalScenarios?.length > 0);
          workshopScore = workshop.operationalScenarios?.length > 0 ? 100 : 0;
          break;
        case '5':
          workshopComplete = (workshop.riskTreatmentPlans?.length > 0);
          workshopScore = workshop.riskTreatmentPlans?.length > 0 ? 100 : 0;
          break;
      }
      
      if (workshopComplete) completeSections++;
      
      sectionDetails[workshopKey] = {
        complete: workshopComplete,
        score: workshopScore,
        status: workshopComplete ? 'Complet' : 'Incomplet'
      };
    });

    const overallScore = totalSections > 0 ? Math.round((completeSections / totalSections) * 100) : 0;
    
    return {
      overallScore,
      totalSections,
      completeSections,
      sectionDetails,
      grade: this.getCompletenessGrade(overallScore),
      recommendations: this.getCompletenessRecommendations(sectionDetails)
    };
  }

  // Helper methods for completeness assessment
  getCompletenessGrade(score) {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  }

  getCompletenessRecommendations(sectionDetails) {
    const recommendations = [];
    
    Object.entries(sectionDetails).forEach(([workshop, details]) => {
      if (!details.complete) {
        const workshopNumber = workshop.replace('workshop', '');
        recommendations.push(`Compléter l'Atelier ${workshopNumber}`);
      }
    });
    
    return recommendations;
  }
}

// Export singleton instance
export default new ReportService();